import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { InfoTooltip } from '../../../index';
import { getPolyLinesFromTemplateStopTemplates } from 'services/util/mapUtils';
import {
  unassignedTemplateStopIconColor,
  IconService,
} from 'services/icon/iconService';
import { useTranslation } from 'react-i18next';
import {
  MarkerCreateFuncs,
  OverlappingMarkerCreateFuncs,
} from '../../MapComponents';
import AddTemplateStopsToTemplateBaseMap from 'components/Maps/BaseMaps/AddTemplateStopsToTemplateBaseMap';
import { getColorByType } from 'services/enums/TemplateStopType';

function AddTemplateStopsToTemplateMap(props) {
  const {
    className,
    unassignedTemplateStops,
    onSelectTemplateStop,
    selectedTemplate,
    addedTemplateStops,
    displayAllTemplates,
    setDisplayAllTemplates,
    removeAssignedStops,
    setRemoveAssignedStops,
    templates,
    mapState,
    setMapState,
  } = props;

  const { t } = useTranslation();

  const [hoveredTemplateStop, setHoveredTemplateStop] = useState(null);

  const highlightTemplateStopRelations = useCallback(
    (templateStop) => {
      // we don't want a rerender, if the template stop is the same
      if (
        hoveredTemplateStop === null ||
        templateStop.id !== hoveredTemplateStop.id
      ) {
        setHoveredTemplateStop(templateStop);
      }
    },
    [hoveredTemplateStop]
  );

  const hideTemplateStopRelations = useCallback(() => {
    setHoveredTemplateStop(null);
  }, []);

  const allTemplatesLabel = (
    <Fragment>
      <InfoTooltip descriptionKey={'80'}>
        <Typography>{t('displayAllTemplates')}</Typography>
      </InfoTooltip>
    </Fragment>
  );

  const removeAssignedStopsLabel = (
    <Fragment>
      <InfoTooltip descriptionKey={'80'}>
        <Typography>{t('removeAssignedStops')}</Typography>
      </InfoTooltip>
    </Fragment>
  );

  return (
    <AddTemplateStopsToTemplateBaseMap
      addedTemplateStops={addedTemplateStops}
      className={className}
      displayAllTemplates={displayAllTemplates}
      displayAllTemplatesLabel={allTemplatesLabel}
      removeAssignedStops={removeAssignedStops}
      removeAssignedStopsLabel={removeAssignedStopsLabel}
      displayControls
      getOverLappingMarkerClusterIcon={(
        overlappingElements,
        highlightDuplicates
      ) =>
        OverlappingMarkerCreateFuncs.getForAddTemplateStopToTemplateMap(
          overlappingElements,
          highlightDuplicates,
          () => {
            return selectedTemplate
              ? selectedTemplate.color
              : unassignedTemplateStopIconColor;
          }
        )
      }
      getPolyLines={getPolyLinesFromTemplateStopTemplates}
      getTemplateClusterIcon={(template) =>
        IconService.getMarkerClusterIcon(template.color, template.id)
      }
      getTemplateStopMarkerAndAddToMcg={(
        template,
        templateStop,
        stopNumber,
        mcg,
        t
      ) =>
        MarkerCreateFuncs.getTemplateStopMarkerTemplateStop({
          template,
          templateStop,
          stopNumber,
          mcg,
          t,
        })
      }
      getToAddTemplateStopMarkerAndAddToMcg={(templateStop, mcg, t) =>
        MarkerCreateFuncs.getUnassignedTemplateStopMarker({
          templateStop,
          mcg,
          t,
          onSelectTemplateStop,
          color: selectedTemplate
            ? selectedTemplate.color
            : getColorByType(templateStop.stopType),
          onHoverStart: highlightTemplateStopRelations,
          onHoverEnd: hideTemplateStopRelations,
        })
      }
      getUnassignedTemplateStopMarkerAndAddToMcg={(templateStop, mcg, t) =>
        MarkerCreateFuncs.getUnassignedTemplateStopMarker({
          templateStop,
          mcg,
          t,
          onSelectTemplateStop,
          color: getColorByType(templateStop.stopType),
          onHoverStart: highlightTemplateStopRelations,
          onHoverEnd: hideTemplateStopRelations,
        })
      }
      hoveredTemplateStop={hoveredTemplateStop}
      setDisplayAllTemplates={setDisplayAllTemplates}
      setRemoveAssignedStops={setRemoveAssignedStops}
      templates={templates}
      unassignedTemplateStops={unassignedTemplateStops}
      mapState={mapState}
      setMapState={setMapState}
    />
  );
}

AddTemplateStopsToTemplateMap.propTypes = {
  addedTemplateStops: PropTypes.array.isRequired,
  className: PropTypes.string,
  displayAllTemplates: PropTypes.bool,
  onSelectTemplateStop: PropTypes.func.isRequired,
  removeAssignedStops: PropTypes.bool,
  setRemoveAssignedStops: PropTypes.func,
  selectedTemplate: PropTypes.object.isRequired,
  setDisplayAllTemplates: PropTypes.func,
  templates: PropTypes.array.isRequired,
  unassignedTemplateStops: PropTypes.array.isRequired,
  mapState: PropTypes.object.isRequired,
  setMapState: PropTypes.func.isRequired,
};

export default AddTemplateStopsToTemplateMap;
